import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";


// Helper function to load cart from local storage
const loadCartFromStorage = () => {
    const storedCart = localStorage.getItem("cart");
    return storedCart ? JSON.parse(storedCart) : {products: [] };  
};

// Helper Function to save cart back to the local Storage
const saveCartToStorage = (cart) => {
    localStorage.setItem("cart", JSON.stringify(cart));
};

// Fetch cart for a user or guest
export const fetchCart = createAsyncThunk("cart/fetchCart", async ({userId, guestId}, {rejectWithValue}) => {
    try {
        const response = await axios.get(`${import.meta.env.VITE_BACKEND_URL}/api/cart`,
            {
                params: { userId, guestId},
            }
        ); 
        return response.data;
    } catch (error) {
        console.error(error);
        return rejectWithValue(error.response.data);
    }
});

// Add an item to the cart for a user or guest
export const addToCart = createAsyncThunk("cart/addToCart", async ({productId, quantity, size, color, guestId, userId}, {rejectWithValue}) => {
    try {
        const response = await axios.post(`${import.meta.env.VITE_BACKEND_URL}/api/cart`, {
            productId, quantity, size, color, guestId, userId,
        });
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response.data);   
    }
});

// Update the quantity of an item in cart
export const updateCartItemQuantity = createAsyncThunk(
    "cart/updateCartItemQuantity", 
    async ({productId, quantity, guestId, userId, size, color}, {rejectWithValue}) => {
        try {
            const response = await axios.put(`${import.meta.env.VITE_BACKEND_URL}/api/cart`, {
                productId,
                quantity,
                guestId,
                userId,
                size,
                color,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Remove an item from the cart
export const removeFromCart = createAsyncThunk(
    "cart/removeFromCart", 
    async ({ productId, guestId, userId, size, color}, {rejectWithValue }) => {
        try {
            const response = await axios({
                method: "DELETE",
                url: `${import.meta.env.VITE_BACKEND_URL}/api/cart`,
                data: { productId, guestId, userId, size, color},
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);   
        }
    }
);

// Merge guest cart into user cart
export const mergeCart = createAsyncThunk("cart/mergeCart", async ({guestId, user}, {rejectWithValue}) => {
    try {
        const response = await axios.post(`${import.meta.env.VITE_BACKEND_URL}/api/cart/merge`, {guestId, user},
            {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("userToken")}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response.data);
    }
}); 

const cartSlice = createSlice({
    name: "cart",
    initialState: {
        cart: loadCartFromStorage(),
        loading: false,
        error: null,
        isCartOpen: false,
    },
    reducers: {
        clearCart: (state) => {
            state.cart = {products: [] };
            localStorage.removeItem("cart"); 
        },
        toggleCart: (state) => {
            state.isCartOpen = !state.isCartOpen;
        },
        openCart: (state) => {
            state.isCartOpen = true;
        },
        closeCart: (state) => {
            state.isCartOpen = false;
        },
        
        // 🚀 NEW: Optimistic quantity update
        optimisticUpdateQuantity: (state, action) => {
            const { productId, quantity, size, color } = action.payload;
            const product = state.cart.products.find(
                p => p.productId === productId && p.size === size && p.color === color
            );
            if (product) {
                product.quantity = quantity;
                saveCartToStorage(state.cart);
            }
        },
        
        // 🚀 NEW: Optimistic remove
        optimisticRemove: (state, action) => {
            const { productId, size, color } = action.payload;
            state.cart.products = state.cart.products.filter(
                p => !(p.productId === productId && p.size === size && p.color === color)
            );
            saveCartToStorage(state.cart);
        },
        
        // 🚀 NEW: Rollback on error
        rollbackCart: (state, action) => {
            state.cart = action.payload;
            saveCartToStorage(action.payload);
        }
    },
    extraReducers: (builder) => {
        builder
        .addCase(fetchCart.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(fetchCart.fulfilled, (state, action) => {
            state.loading = false;
            state.cart = action.payload;
            saveCartToStorage(action.payload);
        })
        .addCase(fetchCart.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to fetch cart ";
        })

        .addCase(addToCart.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(addToCart.fulfilled, (state, action) => {
            state.loading = false;
            state.cart = action.payload;
            saveCartToStorage(action.payload);
        })
        .addCase(addToCart.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload?.message || "Failed to add to cart ";
        })

        // ⚡ Optimized: No loading state for quantity updates
        .addCase(updateCartItemQuantity.pending, (state) => {
            // Don't set loading = true, let optimistic update handle UI
            state.error = null;
        })
        .addCase(updateCartItemQuantity.fulfilled, (state, action) => {
            // Sync with server response (in case of discrepancies)
            state.cart = action.payload;
            saveCartToStorage(action.payload);
        })
        .addCase(updateCartItemQuantity.rejected, (state, action) => {
            state.error = action.payload?.message || "Failed to update item quantity";
            // Cart will be rolled back in the component if needed
        })

        // ⚡ Optimized: No loading state for remove
        .addCase(removeFromCart.pending, (state) => {
            state.error = null;
        })
        .addCase(removeFromCart.fulfilled, (state, action) => {
            state.cart = action.payload;
            saveCartToStorage(action.payload);
        })
        .addCase(removeFromCart.rejected, (state, action) => {
            state.error = action.payload?.message || "Failed to remove items";
        })

        .addCase(mergeCart.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(mergeCart.fulfilled, (state, action) => {
            state.loading = false;
            state.cart = action.payload;
            saveCartToStorage(action.payload);
        })
        .addCase(mergeCart.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload?.message || "Failed to merge cart";
        });
    },
});

export const {
    clearCart, 
    toggleCart, 
    openCart, 
    closeCart,
    optimisticUpdateQuantity,
    optimisticRemove,
    rollbackCart
} = cartSlice.actions;

export default cartSlice.reducer;
